import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader, Card, KPI, Empty, Button } from "@/components/Primitives";
import { StatusPill } from "@/components/StatusPill";
import { Boxes, Users, TrendingUp, Repeat, Rocket, ArrowUpRight, ExternalLink, Sparkles } from "lucide-react";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function statusColor(s) {
  if (s === "active") return "success";
  if (s === "trial") return "primary";
  if (s === "paused") return "warning";
  if (s === "cancelled") return "danger";
  return "default";
}

export default function RevenuePage() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/stats/software-revenue").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  const totals = data?.totals || {};
  const perProduct = data?.per_product || [];
  const perCustomer = data?.per_customer || [];
  const recent = data?.recent_allocations || [];

  const topProductMRR = useMemo(() => Math.max(1, ...perProduct.map((p) => p.mrr || 0)), [perProduct]);
  const topCustomerMRR = useMemo(() => Math.max(1, ...perCustomer.map((p) => p.mrr || 0)), [perCustomer]);

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Studio · Revenue"
        title="Software revenue"
        subtitle="How your products are paying you back. MRR, setup booked, per-product and per-customer."
        actions={
          <Button variant="secondary" onClick={() => nav("/products")} data-testid="manage-software-btn">
            <Boxes className="w-4 h-4" strokeWidth={1.8}/>Manage software
          </Button>
        }
      />

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="revenue-kpis">
        <KPI label="MRR" value={gbp(totals.mrr)} sub={`${totals.active_subscriptions || 0} active subs`} variant="primary" />
        <KPI label="ARR projection" value={gbp(totals.arr)} sub="MRR × 12, flat run-rate" variant="success" />
        <KPI label="Pending pipeline" value={gbp(totals.pending_mrr || 0)} sub={`${totals.pending_subscriptions || 0} awaiting payment · +${gbp(totals.pending_setup || 0)} setup`} variant={(totals.pending_mrr || 0) > 0 ? "warning" : "default"} />
        <KPI label="Setup fees booked" value={gbp(totals.setup_booked)} sub="One-off revenue, all-time" />
      </div>

      {/* Per-product bar chart card */}
      <Card
        title="By product"
        right={<span className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Monthly</span>}
        data-testid="per-product-card"
      >
        {perProduct.length === 0 ? (
          <Empty title="No products yet" subtitle="Add a product and allocate it to a customer to start earning." />
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]">
            {perProduct.map((p) => {
              const pct = ((p.mrr || 0) / topProductMRR) * 100;
              const earning = (p.mrr || 0) > 0;
              return (
                <div key={p.product_id} className="px-5 py-4 flex items-center gap-4 group cursor-pointer hover:bg-[hsl(var(--surface-hover))] transition-colors" onClick={() => nav("/products")} data-testid="per-product-row">
                  {p.product_icon
                    ? <img src={p.product_icon} alt="" className="w-10 h-10 rounded-lg object-cover ring-1 ring-[hsl(var(--border))] shrink-0"/>
                    : <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] grid place-items-center font-bold text-[13px] shrink-0">{(p.product_name || "?").slice(0, 2).toUpperCase()}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[14px] font-semibold truncate">{p.product_name}</div>
                      <StatusPill variant={p.status === "active" ? "success" : p.status === "beta" ? "primary" : "default"}>{p.status || "active"}</StatusPill>
                    </div>
                    <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{p.active_customers || 0} active · {p.total_allocations || 0} allocations</div>
                    <div className="mt-2 h-1.5 bg-[hsl(var(--surface-hover))] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: earning ? "hsl(var(--primary))" : "hsl(var(--border))" }}/>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-[15px]">{gbp(p.mrr)}<span className="text-[11px] font-normal text-[hsl(var(--text-muted))]">/mo</span></div>
                    <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" strokeWidth={2}/>{gbp(p.arr)}/yr</div>
                    {(p.setup_booked || 0) > 0 && <div className="text-[10px] text-[hsl(var(--text-subtle))] mt-0.5">+{gbp(p.setup_booked)} setup</div>}
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[hsl(var(--text-muted))] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" strokeWidth={1.6}/>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Per-customer + recent allocations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Top paying customers" className="lg:col-span-2" right={<Users className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>} data-testid="per-customer-card">
          {perCustomer.length === 0 ? (
            <Empty title="No paying customers yet" subtitle="Allocate a product to a customer from the Software page." />
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                  <th className="text-left font-semibold px-5 py-3">Customer</th>
                  <th className="text-left font-semibold px-5 py-3">Products</th>
                  <th className="text-right font-semibold px-5 py-3">Setup</th>
                  <th className="text-right font-semibold px-5 py-3">MRR</th>
                </tr>
              </thead>
              <tbody>
                {perCustomer.map((c) => {
                  const pct = ((c.mrr || 0) / topCustomerMRR) * 100;
                  return (
                    <tr key={c.customer_id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))] cursor-pointer" onClick={() => nav(`/customers/${c.customer_id}`)} data-testid="per-customer-row">
                      <td className="px-5 py-3 font-medium">{c.customer_name}</td>
                      <td className="px-5 py-3 text-[hsl(var(--text-muted))]">
                        <div className="flex flex-wrap gap-1">
                          {(c.products || []).slice(0, 3).map((pp, i) => (
                            <span key={i} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${pp.status === "active" ? "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]" : "bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]"}`}>
                              {pp.product_name}
                              {pp.status !== "active" && <span className="opacity-70">· {pp.status}</span>}
                            </span>
                          ))}
                          {(c.products || []).length > 3 && <span className="text-[10px] text-[hsl(var(--text-muted))] font-mono">+{c.products.length - 3}</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-[hsl(var(--text-muted))]">{gbp(c.setup)}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="font-mono font-semibold">{gbp(c.mrr)}<span className="text-[10px] font-normal text-[hsl(var(--text-muted))]">/mo</span></div>
                        <div className="mt-1 h-1 bg-[hsl(var(--surface-hover))] rounded-full overflow-hidden">
                          <div className="h-full bg-[hsl(var(--primary))] rounded-full" style={{ width: `${pct}%` }}/>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Subscription mix" right={<Sparkles className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>}>
          <div className="p-5 space-y-4">
            <MixRow label="Active" value={totals.active_subscriptions || 0} tint="hsl(var(--success))" />
            <MixRow label="Awaiting payment" value={totals.pending_subscriptions || 0} tint="hsl(var(--warning))" />
            <MixRow label="Trial" value={totals.trial_subscriptions || 0} tint="hsl(var(--primary))" />
            <MixRow label="Paused" value={totals.paused_subscriptions || 0} tint="hsl(var(--text-muted))" />
            <div className="pt-3 border-t border-[hsl(var(--border))] text-[12px] text-[hsl(var(--text-muted))] leading-relaxed">
              <Repeat className="inline w-3 h-3 mr-1 text-[hsl(var(--primary))]" strokeWidth={2}/>
              Only <span className="font-semibold text-[hsl(var(--text))]">active</span> subscriptions contribute to MRR. Pending allocations auto-activate when their invoice is paid.
            </div>
          </div>
        </Card>
      </div>

      {/* Recent allocations */}
      <Card title="Recent allocations" right={<Rocket className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>} data-testid="recent-allocations-card">
        {recent.length === 0 ? (
          <div className="px-5 py-6 text-[13px] text-[hsl(var(--text-muted))]">Nothing allocated recently. Sell something. Your future self will thank you.</div>
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]">
            {recent.map((r, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 hover:bg-[hsl(var(--surface-hover))] cursor-pointer" onClick={() => r.customer_id && nav(`/customers/${r.customer_id}`)}>
                {r.product_icon
                  ? <img src={r.product_icon} alt="" className="w-8 h-8 rounded-md object-cover ring-1 ring-[hsl(var(--border))] shrink-0"/>
                  : <div className="w-8 h-8 rounded-md bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] grid place-items-center font-bold text-[11px] shrink-0">{(r.product_name || "?").slice(0, 2).toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">
                    <span className="font-semibold">{r.product_name}</span>
                    <span className="text-[hsl(var(--text-muted))]"> → </span>
                    <span>{r.customer_name || "—"}</span>
                  </div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">
                    {r.started_at ? new Date(r.started_at).toLocaleDateString() : "—"} · <StatusPill variant={statusColor(r.status)}>{r.status}</StatusPill>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-semibold text-[13px]">{gbp(r.monthly_fee)}<span className="text-[10px] font-normal text-[hsl(var(--text-muted))]">/mo</span></div>
                  {(r.setup_fee || 0) > 0 && <div className="text-[10px] text-[hsl(var(--text-subtle))]">+{gbp(r.setup_fee)} setup</div>}
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-[hsl(var(--text-muted))] shrink-0" strokeWidth={1.6}/>
              </div>
            ))}
          </div>
        )}
      </Card>

      {!data && <div className="text-[13px] text-[hsl(var(--text-muted))]">Loading…</div>}
    </div>
  );
}

function MixRow({ label, value, tint }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: tint }}/>
        <span className="text-[13px] font-medium">{label}</span>
      </div>
      <span className="font-mono font-semibold text-[15px]">{value}</span>
    </div>
  );
}
